/** @format */

import React from "react";
import {
  MapPin,
  Package,
  CreditCard,
  Calendar,
  Hash,
  User,
  Phone,
  Mail,
} from "lucide-react";
import BlindRender from "../components/BlindRender"; // Assuming path
import {
  getDriveTypeDisplayName,
  getHemRenderPath,
  valanceRender,
} from "./CartItem";

// --- Helper Functions (Can be shared) ---
const formatPrice = (amount, currency = "CAD") => {
  const num = parseFloat(amount) || 0;
  return `$${num.toFixed(2)} ${currency}`;
};
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    return dateString; // Return original if formatting fails
  }
};
// Add other display name helpers if needed (getDriveTypeDisplayName etc.)

// --- Main OrderDetails Component ---
export const OrderDetails = ({ order }) => {
  // Guard clause if order data is not yet available
  if (
    !order ||
    !order.cartItems ||
    !order.shippingAddress ||
    !order.cartSummary
  ) {
    return (
      <div className="text-center text-gray-500">
        Order details loading or unavailable.
      </div>
    );
  }

  const {
    insertId, // Assuming this is the Order ID
    orderDate,
    shippingAddress,
    cartItems, // Assuming items are here
    cartSummary, // Assuming summary is here
    paymentResult,
  } = order;

  const countryCode = shippingAddress.country === "USA" ? "USD" : "CAD";

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 space-y-6 divide-y divide-gray-100">
      {/* --- Section 1: Order Info & Shipping --- */}
      <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Order Info */}
        <div className="space-y-3">
          <h2 className="font-semibold text-lg flex items-center gap-2 text-gray-800">
            <Hash size={18} className="text-primary" /> Order Information
          </h2>
          <dl className="text-sm space-y-1.5">
            <div className="flex">
              <dt className="w-24 font-medium text-gray-600">Order #:</dt>
              <dd className="font-semibold text-gray-900">
                {insertId || "N/A"}
              </dd>
            </div>
            <div className="flex">
              <dt className="w-24 font-medium text-gray-600">Order Date:</dt>
              <dd>{formatDate(orderDate)}</dd>
            </div>
            <div className="flex">
              <dt className="w-24 font-medium text-gray-600">Status:</dt>
              <dd className="capitalize">
                {paymentResult?.status?.toLowerCase() || "Processing"}
              </dd>
            </div>
          </dl>
        </div>
        {/* Shipping Address */}
        <div className="space-y-3">
          <h2 className="font-semibold text-lg flex items-center gap-2 text-gray-800">
            <MapPin size={18} className="text-primary" /> Shipping Address
          </h2>
          <address className="text-sm text-gray-700 not-italic space-y-0.5">
            <p className="font-medium">
              {shippingAddress.firstName} {shippingAddress.lastName}
            </p>
            <p>
              {shippingAddress.addressLine1}
              {shippingAddress.addressLine2 &&
                `, ${shippingAddress.addressLine2}`}
            </p>
            <p>
              {shippingAddress.city}, {shippingAddress.province}{" "}
              {shippingAddress.postalCode}
            </p>
            <p>{shippingAddress.country}</p>
            {shippingAddress.phoneNumber && (
              <p className="pt-1 flex items-center gap-1">
                <Phone size={12} /> {shippingAddress.phoneNumber}
              </p>
            )}
            {shippingAddress.email && (
              <p className="flex items-center gap-1">
                <Mail size={12} /> {shippingAddress.email}
              </p>
            )}
          </address>
        </div>
      </div>

      {/* --- Section 2: Items Ordered --- */}
      <div className="p-5 md:p-6">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-800">
          <Package size={18} className="text-primary" /> Items Ordered (
          {cartItems.length})
        </h2>
        <div className="space-y-4">
          {cartItems.map((item, index) => {
            const productInfo = item.productInfo || {};
            const optionInfo = item.optionInfo || {};
            const isCellular = productInfo.Type === "Simply Cellular";
            const isVerticalSheer = productInfo.Type === "Vertical Sheer";
            const sizeDisplay = `${optionInfo.widthWhole || "?"}" W x ${optionInfo.lengthWhole || "?"}" H`;
            const itemPrice = formatPrice(optionInfo.OUR_PRICE, countryCode); // Price per item
            const itemTotal = formatPrice(
              (parseFloat(optionInfo.OUR_PRICE) || 0) *
                (optionInfo.quantity || 1),
              countryCode,
            );

            return (
              <div
                key={index}
                className="flex gap-4 border-b border-gray-100 pb-4 last:border-none last:pb-0"
              >
                {/* Mini Render */}
                <div className="w-16 h-20 sm:w-20 sm:h-28 rounded-md overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
                  <BlindRender
                    renderFabric={
                      item.renderFabric ||
                      `${productInfo.Fabric?.toLowerCase()} ${productInfo.SG_Color?.toLowerCase()}`.trim()
                    }
                    renderHem={item.renderHem || getHemRenderPath(productInfo)}
                    renderValance={
                      item.renderValance ||
                      valanceRender(
                        productInfo.Valance,
                        productInfo.Valance_Color,
                      ).toLowerCase()
                    }
                    isVerticalSheer={isVerticalSheer}
                  />
                </div>
                {/* Details */}
                <div className="flex-grow text-xs space-y-1">
                  <p className="font-semibold text-sm text-gray-800">
                    {productInfo.Type || "Custom Shade"}
                    {productInfo.OCS_Name && ` - ${productInfo.OCS_Name}`}
                  </p>
                  <p>
                    <span className="font-medium text-gray-600">Fabric:</span>{" "}
                    {productInfo.OCS_Colour || productInfo.SG_Color || "N/A"}
                  </p>
                  <p>
                    <span className="font-medium text-gray-600">Size:</span>{" "}
                    {sizeDisplay}
                  </p>
                  {!isCellular && productInfo.Operation && (
                    <p>
                      <span className="font-medium text-gray-600">
                        Control:
                      </span>{" "}
                      {getDriveTypeDisplayName(productInfo.Operation)} (
                      {productInfo.position})
                    </p>
                  )}
                  {/* Add other key details conditionally */}
                </div>
                {/* Quantity & Price */}
                <div className="text-right text-sm flex-shrink-0 space-y-1">
                  <p className="font-semibold text-gray-800">{itemTotal}</p>
                  <p className="text-gray-500 text-xs">
                    Qty: {optionInfo.quantity || 1}
                  </p>
                  <p className="text-gray-500 text-xs">({itemPrice} each)</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- Section 3: Payment Summary --- */}
      <div className="p-5 md:p-6">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-800">
          <CreditCard size={18} className="text-primary" /> Payment Summary
        </h2>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Subtotal</span>
            <span>
              {formatPrice(
                cartSummary.SUBTOTAL + (cartSummary.withPromoPrice || 0),
                countryCode,
              )}
            </span>
          </div>
          {cartSummary.SAVINGS > 0 &&
            cartSummary.SAVINGS - (cartSummary.withPromoPrice || 0) > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Volume Discount</span>
                <span>
                  -
                  {formatPrice(
                    cartSummary.SAVINGS - (cartSummary.withPromoPrice || 0),
                    countryCode,
                  )}
                </span>
              </div>
            )}
          {cartSummary.withPromoPrice > 0 && (
            <div className="flex justify-between text-blue-600">
              <span>Promo Code ({cartSummary.promoCode})</span>
              <span>
                -{formatPrice(cartSummary.withPromoPrice, countryCode)}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Shipping</span>
            <span>
              {cartSummary.shipping === 0 ? (
                <span className="text-green-600">FREE</span>
              ) : (
                formatPrice(cartSummary.shipping, countryCode)
              )}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Tax</span>
            <span>{formatPrice(cartSummary.FINAL_TAX, countryCode)}</span>
          </div>
          <div className="border-t border-gray-200 mt-2 pt-2">
            <div className="flex justify-between font-semibold text-base text-gray-900">
              <span>Grand Total</span>
              <span>{formatPrice(cartSummary.TOTAL, countryCode)}</span>
            </div>
          </div>
          {/* Payment Method Info */}
          {paymentResult?.method && (
            <div className="text-xs text-gray-500 pt-2">
              Paid via {paymentResult.method}{" "}
              {paymentResult.id && `(Ref: ...${paymentResult.id.slice(-6)})`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
